export type FinanceWorkspaceRole = 'admin' | 'accountant' | 'executive';

export interface FinanceNavSpec {
  key: string;
  label: string;
  routePath: string;
  navPath: string;
  roles: FinanceWorkspaceRole[];
  group: 'finance';
  iconKey: 'dollar' | 'lineChart' | 'bank' | 'calculator';
}

export interface FinanceAuxiliaryRouteSpec {
  key: string;
  label: string;
  routePath: string;
  navPath: string;
  parentKey: 'accounting';
}

export const V2_FINANCE_ROUTE_PATHS = {
  accounting: 'accounting',
  personal1c: 'accounting/personal-1c',
  financeWorkspace: 'accounting/finance-workspace',
  edoRegister: 'accounting/edo',
  reconciliation: 'accounting/reconciliation',
  paymentMatch: 'accounting/payment-match',
  payables: 'accounting/payables',
  invoiceCreate: 'accounting/invoice-create',
  invoiceSend: 'accounting/invoice-send',
  closingDoc: 'accounting/closing-doc',
  bankReconcile: 'accounting/bank-reconcile',
  managementPnl: 'accounting/management-pnl',
  // FLOW[50055]: аналитика по партнёрам (drill-down с карточки «Партнёры» на дашборде)
  partnerAnalytics: 'accounting/partner-analytics',
  costBom: 'accounting/cost-bom',
  partnerBatches: 'accounting/partner-batches',
  quarterlyProfitability: 'accounting/profitability',
  profitabilitySourceExplorer: 'accounting/profitability-sources',
  monthlyFinance: 'accounting/monthly-finance',
  fixedCosts: 'accounting/fixed-costs',
  inputVatCleanup: 'accounting/input-vat-cleanup',
  inputVatUnallocated: 'accounting/input-vat-unallocated',
  procurementVat: 'accounting/procurement-vat',
  taxObligations: 'accounting/tax-obligations',
  legalEntities: 'legal-entities',
} as const;

export const V2_FINANCE_NAV_ITEMS: FinanceNavSpec[] = [
  {
    key: 'accounting',
    label: 'Финансы',
    routePath: V2_FINANCE_ROUTE_PATHS.accounting,
    navPath: '/v2/accounting',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'dollar',
  },
  {
    key: 'accounting-finance-workspace',
    label: 'Очередь финансовых действий',
    routePath: V2_FINANCE_ROUTE_PATHS.financeWorkspace,
    navPath: '/v2/accounting/finance-workspace',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'bank',
  },
  {
    key: 'accounting-edo',
    label: 'ЭДО реестр',
    routePath: V2_FINANCE_ROUTE_PATHS.edoRegister,
    navPath: '/v2/accounting/edo',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'dollar',
  },
  {
    key: 'accounting-reconciliation',
    label: 'Сверка 1С',
    routePath: V2_FINANCE_ROUTE_PATHS.reconciliation,
    navPath: '/v2/accounting/reconciliation',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'dollar',
  },
  {
    key: 'accounting-payment-match',
    label: 'Платёж ↔ счёт',
    routePath: V2_FINANCE_ROUTE_PATHS.paymentMatch,
    navPath: '/v2/accounting/payment-match',
    roles: ['admin', 'accountant'],
    group: 'finance',
    iconKey: 'bank',
  },
  {
    key: 'accounting-payables',
    label: 'Заявки на оплату',
    routePath: V2_FINANCE_ROUTE_PATHS.payables,
    navPath: '/v2/accounting/payables',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'bank',
  },
  {
    key: 'accounting-invoice-create',
    label: 'Выставление счёта',
    routePath: V2_FINANCE_ROUTE_PATHS.invoiceCreate,
    navPath: '/v2/accounting/invoice-create',
    roles: ['admin', 'accountant'],
    group: 'finance',
    iconKey: 'bank',
  },
  {
    key: 'accounting-invoice-send',
    label: 'Отправка счёта (L5)',
    routePath: V2_FINANCE_ROUTE_PATHS.invoiceSend,
    navPath: '/v2/accounting/invoice-send',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'bank',
  },
  {
    key: 'accounting-closing-doc',
    label: 'Черновик УПД (L5)',
    routePath: V2_FINANCE_ROUTE_PATHS.closingDoc,
    navPath: '/v2/accounting/closing-doc',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'bank',
  },
  {
    key: 'accounting-bank-reconcile',
    label: 'Банковская сверка (L4)',
    routePath: V2_FINANCE_ROUTE_PATHS.bankReconcile,
    navPath: '/v2/accounting/bank-reconcile',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'bank',
  },
  {
    key: 'accounting-management-pnl',
    label: 'P&L по годам',
    routePath: V2_FINANCE_ROUTE_PATHS.managementPnl,
    navPath: '/v2/accounting/management-pnl',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'lineChart',
  },
  {
    // FLOW[50055]: экран руководства «сколько партнёр приносит и где не пришли оплаты»
    key: 'accounting-partner-analytics',
    label: 'Аналитика по партнёрам',
    routePath: V2_FINANCE_ROUTE_PATHS.partnerAnalytics,
    navPath: '/v2/accounting/partner-analytics',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'lineChart',
  },
  {
    key: 'accounting-cost-bom',
    label: 'Себестоимость (BOM)',
    routePath: V2_FINANCE_ROUTE_PATHS.costBom,
    navPath: '/v2/accounting/cost-bom',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'calculator',
  },
  {
    key: 'accounting-profitability',
    label: 'Прибыль QTD',
    routePath: V2_FINANCE_ROUTE_PATHS.quarterlyProfitability,
    navPath: '/v2/accounting/profitability',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'lineChart',
  },
  {
    key: 'accounting-monthly-finance',
    label: 'Финансы по месяцам',
    routePath: V2_FINANCE_ROUTE_PATHS.monthlyFinance,
    navPath: '/v2/accounting/monthly-finance',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'lineChart',
  },
  {
    key: 'accounting-fixed-costs',
    label: 'Постоянные расходы',
    routePath: V2_FINANCE_ROUTE_PATHS.fixedCosts,
    navPath: '/v2/accounting/fixed-costs',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'dollar',
  },
  {
    key: 'accounting-input-vat-cleanup',
    label: 'Входящий НДС',
    routePath: V2_FINANCE_ROUTE_PATHS.inputVatCleanup,
    navPath: '/v2/accounting/input-vat-cleanup',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'dollar',
  },
  {
    key: 'accounting-input-vat-unallocated',
    label: 'НДС без распределения',
    routePath: V2_FINANCE_ROUTE_PATHS.inputVatUnallocated,
    navPath: '/v2/accounting/input-vat-unallocated',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'dollar',
  },
  {
    key: 'accounting-procurement-vat',
    label: 'НДС по закупкам',
    routePath: V2_FINANCE_ROUTE_PATHS.procurementVat,
    navPath: '/v2/accounting/procurement-vat',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'dollar',
  },
  {
    key: 'accounting-tax-obligations',
    label: 'Налоговые обязательства',
    routePath: V2_FINANCE_ROUTE_PATHS.taxObligations,
    navPath: '/v2/accounting/tax-obligations',
    roles: ['admin', 'accountant', 'executive'],
    group: 'finance',
    iconKey: 'dollar',
  },
  {
    key: 'legal-entities',
    label: 'Юрлица',
    routePath: V2_FINANCE_ROUTE_PATHS.legalEntities,
    navPath: '/v2/legal-entities',
    roles: ['admin', 'executive'],
    group: 'finance',
    iconKey: 'bank',
  },
];

// K-1.6 (Operator UX 2.0): базовый набор сайдбара. FLOW[45656]: по явному запросу руководителя
// (анализ прибыли за прошлые периоды) в меню выведены «P&L по годам», «Прибыль QTD» и
// «Себестоимость (BOM)» — раньше они были findable только прямым URL. Полный список — за
// VITE_V2_FINANCE_NAV_FULL (см. featureFlags); остальное доступно чип-навигацией лендинга «Финансы».
export const V2_FINANCE_PRIMARY_NAV_KEYS: readonly string[] = [
  'accounting',
  'accounting-management-pnl',
  // FLOW[50055]: аналитика по партнёрам — в основном меню, не только по прямому URL
  'accounting-partner-analytics',
  'accounting-payables',
  'accounting-profitability',
  'accounting-cost-bom',
  'accounting-monthly-finance',
  'accounting-reconciliation',
  'accounting-edo',
];

export const V2_FINANCE_AUXILIARY_ROUTES: FinanceAuxiliaryRouteSpec[] = [
  {
    // FLOW[49640]: пункт меню объявлен в navItems.tsx — у экрана есть роль
    // 'production', которой нет в FinanceWorkspaceRole; здесь он только ради
    // хлебной крошки в разделе «Финансы».
    key: 'accounting-partner-batches',
    label: 'Счета партнёрам',
    routePath: V2_FINANCE_ROUTE_PATHS.partnerBatches,
    navPath: '/v2/accounting/partner-batches',
    parentKey: 'accounting',
  },
  {
    key: 'accounting-profitability-sources',
    label: 'Источники прибыльности',
    routePath: V2_FINANCE_ROUTE_PATHS.profitabilitySourceExplorer,
    navPath: '/v2/accounting/profitability-sources',
    parentKey: 'accounting',
  },
  {
    // FLOW[51206]: экраны объявлены в v2Routes.tsx литералом и открывались из
    // тулбара лендинга без крошки — обратного пути в «Финансы» с них не было.
    key: 'accounting-closing-docs-registry',
    label: 'Реестр закрывающих (1С)',
    routePath: 'accounting/closing-docs-registry',
    navPath: '/v2/accounting/closing-docs-registry',
    parentKey: 'accounting',
  },
  {
    key: 'accounting-payment-orders',
    label: 'Платёжные поручения',
    routePath: 'accounting/payment-orders',
    navPath: '/v2/accounting/payment-orders',
    parentKey: 'accounting',
  },
];
